import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Check, Tag, RotateCcw } from "lucide-react";
import { eurf, Panel } from "./shared";
import OfferCatalogEditor from "./OfferCatalogEditor";

const TIERS = [["starter", "Starter"], ["pro", "Pro"], ["agence", "Agence"]];

export default function OffersView({ token, readOnly }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sim, setSim] = useState({ plan: "starter", example: 1000 });
  const [selOrg, setSelOrg] = useState("");
  const [orgAcc, setOrgAcc] = useState(null);

  const load = () => axios.get(`${API}/superadmin/offers`, h).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    let live = true;
    axios.get(`${API}/superadmin/offers`, h).then(({ data: d }) => { if (live) setData((cur) => cur || d); }).catch(() => {});
    return () => { live = false; };
  }, []); // eslint-disable-line

  if (!data) return <p className="text-zinc-500" data-testid="offers-loading">Chargement des offres…</p>;

  const setPrice = (t, v) => setData((d) => ({ ...d, plan_prices: { ...d.plan_prices, [t]: v } }));
  const setComm = (t, v) => setData((d) => ({ ...d, commission_by_plan: { ...d.commission_by_plan, [t]: v } }));
  const setRO = (patch) => setData((d) => ({ ...d, reactivation_offer: { ...d.reactivation_offer, ...patch } }));
  const setPlanAcc = (tier, patch) => setData((d) => ({ ...d, plan_access: { ...d.plan_access, [tier]: { ...d.plan_access[tier], ...patch } } }));
  const togglePage = (tier, pg) => { const cur = data.plan_access[tier].pages || []; setPlanAcc(tier, { pages: cur.includes(pg) ? cur.filter((x) => x !== pg) : [...cur, pg] }); };
  const toggleMod = (tier, mk) => { const cur = data.plan_access[tier].modules_allowed || []; setPlanAcc(tier, { modules_allowed: cur.includes(mk) ? cur.filter((x) => x !== mk) : [...cur, mk] }); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        plan_prices: Object.fromEntries(TIERS.map(([t]) => [t, parseFloat(data.plan_prices[t]) || 0])),
        commission_by_plan: Object.fromEntries(TIERS.map(([t]) => [t, parseFloat(data.commission_by_plan[t]) || 0])),
        plan_access: data.plan_access,
        reactivation_offer: {
          enabled: !!data.reactivation_offer.enabled,
          discount_pct: parseFloat(data.reactivation_offer.discount_pct) || 0,
          duration_months: parseInt(data.reactivation_offer.duration_months) || 0,
        },
        modules_soon: data.modules_soon || [],
      };
      const { data: fresh } = await axios.put(`${API}/superadmin/offers`, payload, h);
      setData(fresh);
      toast.success("Offres enregistrées");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  // Simulator
  const price = parseFloat(data.plan_prices[sim.plan]) || 0;
  const commPct = parseFloat(data.commission_by_plan[sim.plan]) || 0;
  const amt = parseFloat(sim.example) || 0;
  const myComm = amt * commPct / 100;
  const stripeFee = amt * (data.stripe_fee_pct || 0) / 100 + (data.stripe_fee_fixed || 0);
  const totalTaken = myComm + stripeFee;
  const providerNet = amt - totalTaken;

  // Override
  const loadOrgAcc = (oid) => { setSelOrg(oid); setOrgAcc(null); if (!oid) return; axios.get(`${API}/superadmin/orgs/${oid}/access`, h).then(({ data }) => setOrgAcc(data)).catch(() => {}); };
  const enableOverride = () => setOrgAcc((a) => ({ ...a, override: { pages: [...(a.effective.pages || [])], modules_limit: a.effective.modules_limit, modules_allowed: [...(a.effective.modules_allowed || [])], users_limit: a.effective.users_limit } }));
  const setOv = (patch) => setOrgAcc((a) => ({ ...a, override: { ...a.override, ...patch } }));
  const toggleOvPage = (pg) => { const cur = orgAcc.override.pages || []; setOv({ pages: cur.includes(pg) ? cur.filter((x) => x !== pg) : [...cur, pg] }); };
  const toggleOvMod = (mk) => { const cur = orgAcc.override.modules_allowed || []; setOv({ modules_allowed: cur.includes(mk) ? cur.filter((x) => x !== mk) : [...cur, mk] }); };
  const saveOverride = async () => { try { await axios.put(`${API}/superadmin/orgs/${selOrg}/access`, orgAcc.override, h); toast.success("Surcharge enregistrée"); loadOrgAcc(selOrg); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  const clearOverride = async () => { try { await axios.put(`${API}/superadmin/orgs/${selOrg}/access`, { clear: true }, h); toast.success("Retour aux accès du plan"); loadOrgAcc(selOrg); } catch { toast.error("Erreur"); } };

  const chip = (active, disabled, onClick, label, tid) => (
    <button key={label} data-testid={tid} disabled={disabled} onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs transition-colors ${active ? "border-[#FF6A00]/50 bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-700 text-zinc-400 hover:text-white"} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}>
      {label}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="offers-view">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-white">Offres</h1>
        {!readOnly && <Button data-testid="offers-save" onClick={save} disabled={saving} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Check className="mr-1.5 h-4 w-4" />Enregistrer</Button>}
      </div>
      {readOnly && <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs text-amber-400" data-testid="offers-readonly">Lecture seule — vous n'avez pas le droit de modifier cette section.</div>}

      <OfferCatalogEditor token={token} readOnly={readOnly} onSaved={load} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Prix & commission par plan">
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">Prix mensuel facturé au prestataire et commission plateforme prélevée sur ses ventes (marketplace). Un réglage manuel sur une fiche prestataire reste prioritaire.</p>
            <div className="overflow-hidden rounded-xl border border-zinc-800">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-zinc-800 text-xs uppercase text-zinc-500"><tr><th className="px-3 py-2.5">Plan</th><th className="px-3 py-2.5">Prix €/mois</th><th className="px-3 py-2.5">Commission %</th></tr></thead>
                <tbody>
                  {TIERS.map(([t, l]) => (
                    <tr key={t} className="border-b border-zinc-800/60">
                      <td className="px-3 py-2 font-medium text-white">{l}</td>
                      <td className="px-3 py-2"><Input data-testid={`offers-price-${t}`} type="number" step="1" value={data.plan_prices[t]} onChange={(e) => setPrice(t, e.target.value)} disabled={readOnly} className="h-9 w-28 border-zinc-800 bg-zinc-900 text-white" /></td>
                      <td className="px-3 py-2"><Input data-testid={`offers-comm-${t}`} type="number" step="0.1" value={data.commission_by_plan[t]} onChange={(e) => setComm(t, e.target.value)} disabled={readOnly} className="h-9 w-24 border-zinc-800 bg-zinc-900 text-white" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3 text-xs text-zinc-500">
              <p className="mb-1 font-medium text-zinc-400">Frais Stripe Connect (référence)</p>
              Cartes UE/EEE : <b className="text-zinc-300">1,5 % + 0,25 €</b> · Royaume-Uni : 2,5 % + 0,25 € · Hors EEE : 3,25 % + 0,25 €. Modifiables dans Paramètres.
            </div>
          </div>
        </Panel>

        <Panel title="Commission totale prise (simulateur)">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-zinc-400">Plan simulé</Label>
                <select data-testid="offers-sim-plan" value={sim.plan} onChange={(e) => setSim({ ...sim, plan: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 text-sm text-white">
                  {TIERS.map(([k, l]) => <option key={k} value={k}>{`${l} · ${eurf(parseFloat(data.plan_prices[k]) || 0)}/mois`}</option>)}
                </select>
              </div>
              <div><Label className="text-zinc-400">Montant d'exemple (€)</Label><Input data-testid="offers-sim-example" type="number" value={sim.example} onChange={(e) => setSim({ ...sim, example: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3">
                <span className="text-sm text-zinc-400">Prix du plan</span>
                <span className="font-display font-semibold text-zinc-200">{eurf(price)}/mois</span>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3">
                <span className="text-sm text-zinc-400">Ma commission ({commPct}%)</span>
                <span data-testid="offers-sim-comm" className="font-display font-semibold text-[#FF6A00]">{eurf(myComm)}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3">
                <span className="text-sm text-zinc-400">Frais Stripe ({data.stripe_fee_pct}% + {eurf(data.stripe_fee_fixed)})</span>
                <span data-testid="offers-sim-stripe" className="font-display font-semibold text-sky-400">{eurf(stripeFee)}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-[#FF6A00]/30 bg-[#FF6A00]/10 px-4 py-3">
                <span className="text-sm font-medium text-[#FF6A00]">Commission totale prise</span>
                <span data-testid="offers-sim-total" className="font-display text-lg font-bold text-white">{eurf(totalTaken)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-1 text-xs text-zinc-500">
                <span>Net reversé au prestataire</span>
                <span className="text-emerald-400">{eurf(providerNet)}</span>
              </div>
            </div>
          </div>
        </Panel>
      </div>

      <Panel title="Offre de retour (client en pause / résilié)">
        <div className="space-y-4">
          <label className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900 p-3 text-sm text-zinc-300">
            <span>Activer l'offre de retour dans l'email de réactivation</span>
            <Switch data-testid="offers-reactivation-enabled" checked={!!data.reactivation_offer.enabled} onCheckedChange={(v) => setRO({ enabled: v })} disabled={readOnly} />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-zinc-400">Remise (%)</Label><Input data-testid="offers-reactivation-discount" type="number" step="1" value={data.reactivation_offer.discount_pct} onChange={(e) => setRO({ discount_pct: e.target.value })} disabled={readOnly} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
            <div><Label className="text-zinc-400">Durée (mois)</Label><Input data-testid="offers-reactivation-duration" type="number" step="1" value={data.reactivation_offer.duration_months} onChange={(e) => setRO({ duration_months: e.target.value })} disabled={readOnly} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
          </div>
          <p className="text-xs text-zinc-500">Ces valeurs alimentent les variables <code className="rounded bg-zinc-800 px-1 text-zinc-300">{`{{discount}}`}</code> et <code className="rounded bg-zinc-800 px-1 text-zinc-300">{`{{duration}}`}</code> de l'email « Réactivation » (éditable dans Personnalisation).</p>
        </div>
      </Panel>

      <Panel title="Disponibilité des modules">
        <div className="space-y-3" data-testid="offers-modules-availability">
          <p className="text-xs text-zinc-500">Un module désactivé affiche « Disponible bientôt » dans l'app (non activable, retiré de la navigation) et sur le site vitrine. Les réglages des prestataires sont conservés et reviennent dès la réactivation.</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {data.meta.modules.map((m) => {
              const soon = (data.modules_soon || []).includes(m.key);
              return (
                <label key={m.key} data-testid={`offers-module-${m.key}`} className={`flex items-center justify-between gap-3 rounded-xl border p-3 text-sm transition-colors ${soon ? "border-zinc-800 bg-zinc-900/50 text-zinc-500" : "border-zinc-800 bg-zinc-900 text-zinc-200"}`}>
                  <span className="flex flex-col">{m.label}<span data-testid={`offers-module-${m.key}-status`} className={`text-[11px] ${soon ? "soon-text" : "ok-text"}`}>{soon ? "Disponible bientôt" : "Disponible"}</span></span>
                  <Switch data-testid={`offers-module-${m.key}-switch`} checked={!soon} disabled={readOnly}
                    onCheckedChange={(on) => setData((d) => ({ ...d, modules_soon: on ? (d.modules_soon || []).filter((x) => x !== m.key) : [...(d.modules_soon || []), m.key] }))} />
                </label>
              );
            })}
          </div>
          <p className="text-xs text-zinc-500">Enregistré avec le bouton « Enregistrer » en haut de page.</p>
        </div>
      </Panel>

      <Panel title="Accès & modules par abonnement">
        <div className="space-y-5">
          <p className="text-xs text-zinc-500">Définissez, pour chaque plan, les pages accessibles et le nombre de modules activables. S'applique automatiquement à tous les prestataires du plan (sauf surcharge individuelle ci-dessous).</p>
          <div className="grid gap-4 lg:grid-cols-3">
            {TIERS.map(([tier, label]) => {
              const pa = data.plan_access[tier] || {};
              return (
                <div key={tier} data-testid={`offers-plan-${tier}`} className="space-y-3 rounded-2xl border border-zinc-800 bg-zinc-950 p-4">
                  <p className="font-display text-lg font-bold text-white">{label}</p>
                  <div>
                    <Label className="text-xs uppercase text-zinc-500">Pages</Label>
                    <div className="mt-2 flex flex-wrap gap-1.5">{data.meta.pages.map((p) => chip((pa.pages || []).includes(p.key), readOnly, () => togglePage(tier, p.key), p.label, `offers-plan-${tier}-page-${p.key}`))}</div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-zinc-500">Nombre de modules activables</Label>
                    <Input data-testid={`offers-plan-${tier}-limit`} type="number" min="0" disabled={readOnly} value={pa.modules_limit ?? 0} onChange={(e) => setPlanAcc(tier, { modules_limit: parseInt(e.target.value) || 0 })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-zinc-500">Nombre d'utilisateurs</Label>
                    <Input data-testid={`offers-plan-${tier}-users`} type="number" min="0" disabled={readOnly} value={pa.users_limit ?? 0} onChange={(e) => setPlanAcc(tier, { users_limit: parseInt(e.target.value) || 0 })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-zinc-500">Modules autorisés</Label>
                    <div className="mt-2 flex flex-wrap gap-1.5">{data.meta.modules.map((m) => chip((pa.modules_allowed || []).includes(m.key), readOnly, () => toggleMod(tier, m.key), m.label, `offers-plan-${tier}-mod-${m.key}`))}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-zinc-500">Les prix, commissions, accès et l'offre de retour sont enregistrés ensemble via le bouton « Enregistrer » en haut de page.</p>
        </div>
      </Panel>

      <Panel title="Surcharge par prestataire">
        <div className="space-y-4">
          <p className="text-xs text-zinc-500">Choisissez un prestataire pour lui appliquer des accès spécifiques, indépendamment de son plan.</p>
          <select data-testid="sa-override-org" value={selOrg} onChange={(e) => loadOrgAcc(e.target.value)} className="h-10 w-full max-w-md rounded-md border border-zinc-800 bg-zinc-900 px-3 text-sm text-white">
            <option value="">— Sélectionner un prestataire —</option>
            {data.orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          {selOrg && orgAcc && (
            <div className="space-y-3 rounded-2xl border border-zinc-800 bg-zinc-950 p-4" data-testid="sa-override-panel">
              <p className="text-sm text-zinc-400">Plan : <b className="text-white">{orgAcc.plan || "—"}</b> · {orgAcc.override ? <span className="text-[#FF6A00]">Surcharge manuelle active</span> : <span className="text-zinc-500">Suit les accès du plan</span>}</p>
              {!orgAcc.override ? (
                <Button data-testid="sa-override-enable" onClick={enableOverride} disabled={readOnly} className="rounded-full bg-zinc-800 text-white hover:bg-zinc-700">Activer une surcharge manuelle</Button>
              ) : (
                <>
                  <div>
                    <Label className="text-xs uppercase text-zinc-500">Pages</Label>
                    <div className="mt-2 flex flex-wrap gap-1.5">{data.meta.pages.map((p) => chip((orgAcc.override.pages || []).includes(p.key), readOnly, () => toggleOvPage(p.key), p.label, `sa-ov-page-${p.key}`))}</div>
                  </div>
                  <div className="max-w-xs">
                    <Label className="text-xs uppercase text-zinc-500">Nombre de modules activables</Label>
                    <Input data-testid="sa-ov-limit" type="number" min="0" disabled={readOnly} value={orgAcc.override.modules_limit ?? 0} onChange={(e) => setOv({ modules_limit: parseInt(e.target.value) || 0 })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" />
                  </div>
                  <div className="max-w-xs">
                    <Label className="text-xs uppercase text-zinc-500">Nombre d'utilisateurs</Label>
                    <Input data-testid="sa-ov-users" type="number" min="0" disabled={readOnly} value={orgAcc.override.users_limit ?? 0} onChange={(e) => setOv({ users_limit: parseInt(e.target.value) || 0 })} className="mt-1 border-zinc-800 bg-zinc-900 text-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-zinc-500">Modules autorisés</Label>
                    <div className="mt-2 flex flex-wrap gap-1.5">{data.meta.modules.map((m) => chip((orgAcc.override.modules_allowed || []).includes(m.key), readOnly, () => toggleOvMod(m.key), m.label, `sa-ov-mod-${m.key}`))}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid="sa-override-save" onClick={saveOverride} disabled={readOnly} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Check className="mr-1.5 h-4 w-4" />Enregistrer la surcharge</Button>
                    <Button data-testid="sa-override-clear" onClick={clearOverride} disabled={readOnly} variant="outline" className="rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"><RotateCcw className="mr-1.5 h-4 w-4" />Revenir au plan</Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
